// Gemeinsame Bausteine der Inventur-Verträge. Mengen stehen überall in Tausendstel der BASISEINHEIT des Artikels
// (1 Flasche = 1000); nur beim Zählen nennt man zusätzlich das Gebinde (`packQty` in Tausendstel des Gebindes).
import { zId } from '@shared/ids'
import { z } from 'zod'

export const COUNT_TYPE = 'stock_count'

/** Verfahren: Stichtagsinventur · permanente (rollierende) Inventur · Kontrollzählung einzelner Artikel */
export const COUNT_KINDS = ['full', 'rolling', 'spot'] as const
export type CountKind = (typeof COUNT_KINDS)[number]

export const COUNT_STATUS = {
  draft: 'draft',
  counting: 'counting',
  review: 'review',
  posted: 'posted',
  closed: 'closed',
  cancelled: 'cancelled',
} as const
export type CountStatus = (typeof COUNT_STATUS)[keyof typeof COUNT_STATUS]
export const COUNT_STATUSES = Object.values(COUNT_STATUS) as [CountStatus, ...CountStatus[]]

export const READ = 'stocktaking.count.read'
export const CREATE = 'stocktaking.count.create'
export const ENTER = 'stocktaking.count.enter'
export const APPROVE = 'stocktaking.count.approve'
export const LIMIT_KEY = 'maxDifferenceCents'

/** Kalendertag 'YYYY-MM-DD' (Europe/Berlin). */
export const zDay = z.iso.date({ error: 'Bitte ein gültiges Datum eingeben.' })

export const zNote = z.string().trim().max(500).nullish()

export const zReason = z
  .string({ error: 'Bitte geben Sie einen Grund an.' })
  .trim()
  .min(3, { error: 'Bitte geben Sie einen Grund an (mindestens 3 Zeichen).' })
  .max(500)

/** gezählte Menge in Tausendstel – null ist eine gültige Zählung („nichts da“) */
export const zCountQty = z
  .int({ error: 'Bitte eine Menge eingeben.' })
  .min(0, { error: 'Die Menge darf nicht negativ sein.' })
  .max(1_000_000_000, { error: 'Die Menge ist zu groß.' })

export const zPerson = z.object({ id: zId, name: z.string() })
export type Person = z.output<typeof zPerson>

export const zLocationRef = z.object({ id: zId, code: z.string(), name: z.string() })

/** Gebinde eines Artikels für Anzeige und Umrechnung. */
export const zUnitInfo = z.object({
  id: zId,
  /** Kurzform: „Ka“ */
  short: z.string(),
  /** Basismenge je Gebinde in Tausendstel (Kasten mit 20 Flaschen = 20000) */
  baseQty: z.int(),
})
export type UnitInfo = z.output<typeof zUnitInfo>

/** Artikel, wie ihn Zählliste und Differenzliste brauchen. */
export const zItemRef = z.object({
  id: zId,
  sku: z.string(),
  name: z.string(),
  /** Kurzform der Basiseinheit: „Fl“ */
  baseUnit: z.string(),
  /** Lagergebinde (meist der Kasten) – darin zeigt und erfasst das Lager Mengen */
  unit: zUnitInfo,
  lotTracked: z.boolean(),
  /** der Artikel führt ein Mindesthaltbarkeits- oder Verbrauchsdatum */
  dated: z.boolean(),
})
export type ItemRef = z.output<typeof zItemRef>

export const zBinRef = z.object({ id: zId, code: z.string(), zone: z.string() })

export const zProgress = z.object({
  binsTotal: z.int(),
  binsCounted: z.int(),
  /** Positionen (Lagerplatz × Artikel × Charge) der Inventur und wie viele davon gezählt sind */
  positions: z.int(),
  positionsCounted: z.int(),
  recountsOpen: z.int(),
})
